import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import config from "../../config";
import API from "../../axios/API";
import _ from "lodash";

const SiteContext = createContext();

export const useSiteContext = () => {
  return useContext(SiteContext);
};

/**
 * This component provides a context for managing site-related data and state.
 *
 * @returns {ReactNode} A React element that provides the site context.
 */

export const SiteProvider = ({ children }) => {
  /**
   * State variables for managing site data and context.
   */
  const [countryItem, setCountryItem] = useState("IN");
  const [pageName, setPageName] = useState("Information");
  const [activeStep, setActiveStep] = useState(0);
  const [isCreatingSite, setIsCreatingSite] = useState(true); // Initialize to true for creating new brand
  const [siteStatus, setSiteStatus] = useState({
    informationComplete: false,
    configurationComplete: false,
    siteAccess: false,
  });
  const [siteViewRefresh, setSiteViewRefresh] = useState(false);
  const [companyMailUnique, setCompanyMailUnique] = useState(true);
  /**
   * Retrieves initial site data.
   *
   * @returns {Object} Initial site data object.
   */
  function getInitialSiteData() {
    return {
      siteId: 0,
      siteName: "",
      domain: "",
      placement: "",
      mappingURL: "",
      inventoryTypeDTO: {
        inventoryTypeId: "",
        inventoryType: "",
      },
      languageDTO: {
        languageId: "",
        languageName: "",
      },
      siteCategoryDTO: {
        categoryId: "",
        categoryName: "",
        siteSubCategory: [
          {
            subCategoryId: "",
            subCategoryName: "",
          },
        ],
      },
      country: "IN",
      bundle: "",
      androidStoreURL: "",
      iosStoreURL: "",
      ipAddress: "",
      acceptHeader: "",
      userAgent: "",
      modifiedTime: "",
      modifiedBy: "",
      metaKeywords: "",
      siteURL: "",
      partner: {
        partnerId: "",
      },
      highVolumeSite: 0,
      availableDIY: 1,
      premiumSite: 0,
      apiBackfillFlag: 0,
      pmaxSite: 1,
      siteConfigDTO: {
        siteConfigId: 0,
        splashdDC: {
          sitedcId: 0,
          deliveryChannelId: "",
          pricingModel: "",
          price: null,
          status: 0, //In-Image_D
          adgebraCommission: 10,
          dynamicRevShareFlag: 0,
          dynamicRevSharePercentage: null,
        },
        splashmDC: {
          sitedcId: 0,
          deliveryChannelId: "",
          pricingModel: "",
          price: null,
          status: 0, //In-Image_M
          adgebraCommission: 10,
        },
        spicedDC: {
          sitedcId: 0,
          deliveryChannelId: "",
          pricingModel: "",
          price: null,
          status: 0, //In-Footer_D
          adgebraCommission: 10,
        },
        spicemDC: {
          sitedcId: 0,
          deliveryChannelId: "",
          pricingModel: "",
          price: null,
          status: 0, //In-Footer_M
          adgebraCommission: 10,
        },
        sparkDC: {
          sitedcId: 0,
          deliveryChannelId: "",
          pricingModel: "",
          price: null,
          status: 0, //In-Screen
          adgebraCommission: 10,
        },
        nativeDC: {
          sitedcId: 0,
          deliveryChannelId: 9,
          pricingModel: 7,
          price: "",
          defaultECPM: 0,
          maxECPMLimit: 0,
          dynamicECPMFlag: 1,
          cpmPriceFlag: 0,
          impressionTrackerFlag: 0,
          videoFlag: 0,
          status: 0, // NativeDC
        },
        bannerDC: {
          sitedcId: 0,
          deliveryChannelId: 15,
          pricingModel: 7,
          price: "",
          defaultECPM: 0,
          maxECPMLimit: 0,
          dynamicECPMFlag: 1,
          cpmPriceFlag: 0,
          impressionTrackerFlag: 0,
          videoFlag: 0,
          status: 0,
        },
      },
      user:
        activeStep === 2
          ? [
              {
                userId: 0,
                userName: null,
                password: null,
                roleId: null,
                firstName: null,
                status: null,
                accountId: null,
                partnerId: null,
                languageId: null,
                companyFlag: null,
                statusFlag: null,
                trafficPlusFlag: "",
                faqsFlag: "",
                userTypeId: null,
                modifiedTime: null,
                modifiedBy: null,
                createdBy: null,
                primaryFlag: "",
                contacts: [
                  {
                    contactId: 0,
                    contactName: "",
                    street1: null,
                    street2: null,
                    city: null,
                    zip: null,
                    state: null,
                    country: null,
                    contactNo: null,
                    email: "",
                    linkedIn: null,
                    skype: null,
                  },
                ],
              },
            ]
          : null,
      dcFlags: {
        splashdFlag: "",
        splashmFlag: "",
        spicedFlag: "",
        spicemFlag: "",
      },
      diyTags: {
        tag: "",
        mtsize_320x50: "",
        mtsize_320x100: "",
        mtsize_300x250: "",
        mtsize_300x600: "",
        dtsize_160x600: "",
        dtsize_300x250: "",
        dtsize_300x600: "",
        dtsize_728x90: "",
        wpsize_690x210: "",
        wpsize_690x420: "",
        wpsize_690x630: "",
      },
      activeStep: 0,
    };
  }

  const [siteData, setSiteData] = useState(getInitialSiteData());
  const [part, setPart] = useState();
  const [siteId, setSideId] = useState();
  const [mappingURL, setMappingURL] = useState(siteData.mappingURL);
  const [contactIdForUnique, setContactIdUnique] = useState(0);

  /**
   * Resets the site data to its initial state.
   *
   * @returns {void}
   */
  const resetSiteData = () => {
    return null;
  };

  /**
   * Effect hook to update site status based on site data changes.
   */
  useEffect(() => {
    // Check if brandId is other than 0
    if (siteData.siteId !== 0) {
      setSiteStatus((prev) => ({
        ...prev,
        informationComplete: true,
      }));
    } else {
      setSiteStatus((prev) => ({
        ...prev,
        informationComplete: false,
      }));
    }
  }, [siteData]);

  /**
   * Checks the uniqueness of a mapping URL.
   *
   * @param {string} mappingURL1 The mapping URL to check.
   * @returns {string|undefined} Error message if not unique, otherwise undefined.
   */
  const checkSiteNameUniqueness = async (mappingURL1) => {
    // console.log("mappingURL1", mappingURL1);
    // console.log("partner Id", part);
    // console.log("Site Id", siteId);
    try {
      if(mappingURL1 && part){
      // Create a new FormData object
      const formData = new FormData();

      // Append the brandName and brandId as key-value pairs
      formData.append("partnerId", part || 0);
      formData.append("mappingUrl", mappingURL1); // Convert brandId to a string and append it
      formData.append("siteId", siteId);

      const headers = {
        "Content-Type": "multipart/form-data", // Set the content type to form-data
      };

      // Make an API call with the FormData
      const response = await API.post(
        config.baseUrl + "/siteservice/site/findduplicateurl",
        formData,
        {
          headers: headers,
        }
      );

      // console.log("----->", response.data);

      if (response.data.errorCode === 0) {
        return undefined; // Name is unique
      } else {
        return "Mapping URL must be unique";
      }
    }
    } catch (error) {
      console.error(error);
      return "Error occurred while checking uniqueness";
    }
  };

  /**
   * Checks the uniqueness of a domain URL.
   *
   * @param {string} domainURL1 The domain URL to check.
   * @returns {string|undefined} Error message if not unique, otherwise undefined.
   */
  const checkSiteDomainUniqueness = async (domainURL1) => {
    // console.log(domainURL1)
    // console.log("domainURL1", domainURL1);
    // console.log("partner Id", part);
    // console.log("Site Id", siteId);
    try {
      if(domainURL1 && part){
      // Create a new FormData object
      const formData = new FormData();

      // Append the brandName and brandId as key-value pairs
      formData.append("partnerId", part || 0);
      formData.append("domainUrl", domainURL1); // Convert brandId to a string and append it
      formData.append("siteId", siteId);

      const headers = {
        "Content-Type": "multipart/form-data", // Set the content type to form-data
      };

      // Make an API call with the FormData
      const response = await API.post(
        config.baseUrl + "/siteservice/site/findduplicatedomain",
        formData,
        {
          headers: headers,
        }
      );

      console.log("----->", response.data);

      if (response.data.errorCode === 0) {
        return undefined; // Name is unique
      } else {
        return "Domain URL must be unique";
      }
    }
    } catch (error) {
      console.error(error);
      return "Error occurred while checking uniqueness";
    }
  };


  /**
   * Checks the uniqueness of an email.
   *
   * @param {string} email The email to check.
   * @returns {string|undefined} Error message if not unique, otherwise undefined.
   */
  const checkSiteEmailUniqueness = async (email) => {
    // console.log("email", email);
    // console.log("contactId", contactIdForUnique);
    try {
      // Create a new FormData object
      const formData = new FormData();
      // Append the brandName and brandId as key-value pairs
      formData.append("email", email);
      formData.append("contactId", contactIdForUnique);

      // Make an API call with the FormData
      const response = await API.post(
        config.baseUrl + "/siteservice/site/find-duplicate-email",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data", // Set the content type to form-data
          },
        }
      );

      // console.log("----->", response.data);

      if (response.data.errorCode === 0) {
        return undefined; // Name is unique
      } else {
        return "Email Already Exists!";
      }
    } catch (error) {
      return "Error occurred while checking uniqueness";
    }
  };

  
  const [lastestValueUpdated, setLastestValueUpdated] = useState({
    sparkDC: false,
    nativeDC: false,
    splashdDC: false,
    splashmDC: false,
    spicedDC: false,
    spicemDC: false,
    bannerDC: false,
  });

  /**
   * Resets the context to its initial state.
   *
   * @returns {void}
   */
  const clearContext = () => {
    setCountryItem("India");
    setActiveStep(0);
    setIsCreatingSite(true);
    setSiteData(getInitialSiteData());
    setMappingURL("");
    setPageName("Information");
    lastestValueUpdated.sparkDC = false;
    lastestValueUpdated.nativeDC = false;
    lastestValueUpdated.splashdDC = false;
    lastestValueUpdated.splashmDC = false;
    lastestValueUpdated.spicedDC = false;
    lastestValueUpdated.spicemDC = false;
    lastestValueUpdated.siteAccess = false;
    lastestValueUpdated.bannerDC = false;
  };

  const compareJSON = (json1, json2) => {
    // Use lodash's isEqual function for deep comparison
    return _.isEqual(json1, json2);
  };

  return (
    <SiteContext.Provider
      value={{
        siteData,
        setSiteData,
        activeStep,
        setActiveStep,
        pageName,
        setPageName,
        resetSiteData,
        countryItem,
        setCountryItem,
        isCreatingSite,
        setIsCreatingSite,
        mappingURL,
        setMappingURL,
        clearContext,
        siteStatus,
        setSiteStatus,
        checkSiteNameUniqueness,
        checkSiteDomainUniqueness,
        setPart,
        setSideId,
        lastestValueUpdated,
        setLastestValueUpdated,
        checkSiteEmailUniqueness,
        setContactIdUnique,
        companyMailUnique,
        setCompanyMailUnique,
        compareJSON,
        contactIdForUnique,
        siteViewRefresh,
        setSiteViewRefresh,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};
